import { memo, useEffect, useRef, useState } from "react";
import { Trans, useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { BodyCopy } from "../../components/BodyCopy";
import {
  AuraLayout,
  Button,
  GradientText,
  Layout,
  PageTitle,
} from "lola-framework-ui-test";
import { FloatingField } from "../../components/FloatingField";
import { ErrorAlertIcon as ErrorIcon } from "../../assets/icons";
import { FlowHeroMark } from "../../components/FlowHeroMark";
import { SocialAuthButton } from "../../components/consumer-auth/SocialAuthButton";
import {
  OTP_LENGTH,
  OtpCodeInput,
} from "../../components/consumer-auth/OtpCodeInput";
import { APP_ROUTES, CONSUMER_AUTH_ROUTES, TITLE_COLOR } from "../../constants";
import { useGalleryStore } from "../../gallery/galleryStore";
import { useStore } from "../../store/globalState";
import { ScrollFadeIndicator } from "../../components/ScrollFadeIndicator";
import { Navbar } from "../../components/Navbar";

export type ConsumerAuthVariant =
  | "form"
  | "formAfterLogout"
  | "formBindFailed"
  | "code"
  | "codeResendReady"
  | "codeIncorrect"
  | "binding"
  | "missingKey"
  | "pending"
  | "errorBind"
  | "errorGeneric";

const FORM_VARIANTS: ConsumerAuthVariant[] = [
  "form",
  "formAfterLogout",
  "formBindFailed",
  "code",
  "codeResendReady",
  "codeIncorrect",
  "pending",
  "errorGeneric",
];

const GALLERY_OTP_COOLDOWN_SEC = 30;

const formatResendClock = (remaining: number): string => {
  const total = Math.max(0, Math.floor(remaining));
  const minutes = Math.floor(total / 60);
  const seconds = total % 60;
  return `${String(minutes).padStart(2, "0")}:${String(seconds).padStart(2, "0")}`;
};

/**
 * Gallery-safe Consumer Clerk auth. Variants freeze AuthPage states from
 * fund-request — no Clerk, no APIs. Continue navigates between gallery
 * routes so each screen stays inspectable.
 */
export const AuthPage = memo(({ variant }: { variant: ConsumerAuthVariant }) => {
  const { t } = useTranslation("consumer-auth");
  const { theme } = useStore();
  const navigate = useNavigate();
  const exporting = useGalleryStore((state) => state.exporting);
  const colorConfig = theme.whiteView;
  const {
    background,
    backgroundBtn,
    textColorBtn,
    bodyCopy,
    footerColor,
    errorColor,
  } = colorConfig;
  const fieldColors = {
    background: colorConfig.background,
    title: colorConfig.title,
    bodyCopy,
    inactiveColor: colorConfig.subtitile,
    errorColor: colorConfig.errorColor,
  };
  const [email, setEmail] = useState("");
  const [code, setCode] = useState(
    variant === "codeIncorrect" ? "123456" : "",
  );
  const [resendRemaining, setResendRemaining] = useState(
    GALLERY_OTP_COOLDOWN_SEC,
  );
  const verifyCtaRef = useRef<HTMLElement>(null);
  const codeCompleteRef = useRef(code.length === OTP_LENGTH);

  // Completing the code hands focus to the CTA, which also drops the OTP ring.
  // Runs after commit so the button is already enabled, and only on the
  // transition, so landing on a screen with a filled code steals nothing.
  useEffect(() => {
    const complete = code.length === OTP_LENGTH;
    if (complete && !codeCompleteRef.current) {
      verifyCtaRef.current?.querySelector("button")?.focus();
    }
    codeCompleteRef.current = complete;
  }, [code]);

  useEffect(() => {
    if (variant !== "code") return;
    setResendRemaining(GALLERY_OTP_COOLDOWN_SEC);
  }, [variant]);

  useEffect(() => {
    if (variant !== "code" || exporting) return undefined;
    if (resendRemaining <= 0) {
      navigate(CONSUMER_AUTH_ROUTES.CODE_RESEND);
      return undefined;
    }
    const id = window.setTimeout(() => {
      setResendRemaining((current) => Math.max(0, current - 1));
    }, 1000);
    return () => window.clearTimeout(id);
  }, [exporting, navigate, resendRemaining, variant]);

  const isCode =
    variant === "code" ||
    variant === "codeResendReady" ||
    variant === "codeIncorrect";
  const isCtaDisabled = isCode && code.length !== OTP_LENGTH;
  const canResend = variant === "codeResendReady";
  const waitClock =
    variant === "code"
      ? formatResendClock(resendRemaining)
      : formatResendClock(GALLERY_OTP_COOLDOWN_SEC);
  const fromClaim = variant !== "formAfterLogout";
  const showBack = isCode || fromClaim;
  const codeError =
    variant === "codeIncorrect" ? t("errorCodeIncorrect") : null;
  const formError =
    variant === "pending"
      ? t("errorPendingSession")
      : variant === "errorGeneric"
        ? t("errorGeneric")
        : variant === "formBindFailed"
          ? t("errorBind")
          : null;

  if (variant === "missingKey" || variant === "errorBind") {
    const errorConfig = theme.errorView;
    const {
      backgroundBtn: errorBackgroundBtn,
      textColorBtn: errorTextColorBtn,
      title: errorTitle,
      bodyCopy: errorBody,
      iconColors,
      backgroundIcon,
    } = errorConfig;

    return (
      <AuraLayout colorConfig={errorConfig} className="flow-landing-page">
        <Layout.Content className="landing-section flow-hero-section">
          <ScrollFadeIndicator color={errorConfig.bodyCopy} />
          <section className="flow-hero">
            <FlowHeroMark backgroundIcon={backgroundIcon}>
              <ErrorIcon size={60} colors={iconColors} />
            </FlowHeroMark>
            <section className="landing-container">
              <PageTitle
                highlight={t("error.title", { ns: "translation" })}
                highlightColor={errorTitle}
                secudnary=""
                secudnaryColor={errorTitle}
              />
              <BodyCopy textColor={errorBody}>
                {variant === "missingKey" ? t("missingKey") : t("errorBind")}
              </BodyCopy>
            </section>
          </section>
        </Layout.Content>
        <Layout.Footer>
          <section className="footer-section">
            <Button
              size="large"
              background={errorBackgroundBtn}
              color={errorTextColorBtn}
              onClick={() => navigate(CONSUMER_AUTH_ROUTES.SIGN_IN)}
            >
              {t("common.retry", { ns: "translation" })}
            </Button>
          </section>
        </Layout.Footer>
      </AuraLayout>
    );
  }

  if (variant === "binding") {
    return (
      <AuraLayout colorConfig={colorConfig}>
        <Layout.Content>
          <div className="landing-section content-center loader-page">
            <section className="content">
              <div
                className="loader-spinner"
                role="status"
                aria-label={t("loading", { ns: "translation" })}
              />
              <BodyCopy textColor={TITLE_COLOR}>{t("binding")}</BodyCopy>
            </section>
          </div>
        </Layout.Content>
      </AuraLayout>
    );
  }

  if (!FORM_VARIANTS.includes(variant)) {
    return null;
  }

  const handleBack = () => {
    if (isCode) {
      navigate(CONSUMER_AUTH_ROUTES.SIGN_IN);
      return;
    }
    navigate(APP_ROUTES.FUND_REQUEST_PAGE);
  };

  return (
    <AuraLayout colorConfig={colorConfig}>
      {showBack && (
        <Layout.Header>
          <Navbar onBackClick={handleBack} />
        </Layout.Header>
      )}
      <Layout.Content className="landing-section consumer-auth-section">
        <ScrollFadeIndicator color={bodyCopy} />
        <section className="consumer-auth-form">
          <header className="consumer-auth-header">
            <GradientText as="h2" textColor={TITLE_COLOR}>
              <Trans ns="consumer-auth" i18nKey={isCode ? "titleCode" : "title"} />
            </GradientText>
            {!isCode && (
              <BodyCopy as="p" textColor={bodyCopy}>
                <Trans ns="consumer-auth" i18nKey="subtitle" />
              </BodyCopy>
            )}
          </header>

          <div className="consumer-auth-main">
            {!isCode && (
              <>
                <div className="consumer-auth-socials">
                  <SocialAuthButton
                    provider="google"
                    label={t("google")}
                    textColor={TITLE_COLOR}
                    background={background}
                    onClick={() => undefined}
                  />
                  <SocialAuthButton
                    provider="microsoft"
                    label={t("microsoft")}
                    textColor={TITLE_COLOR}
                    background={background}
                    onClick={() => undefined}
                  />
                </div>
                <div className="consumer-auth-or" style={{ color: footerColor }}>
                  <BodyCopy
                    as="span"
                    textColor={footerColor}
                    className="consumer-auth-or-label"
                  >
                    {t("or")}
                  </BodyCopy>
                </div>
              </>
            )}

            {isCode ? (
              <div className="otp-code-field">
                <BodyCopy
                  as="p"
                  textColor={codeError ? errorColor ?? TITLE_COLOR : footerColor}
                  className={`otp-code-hint${codeError ? " is-error" : ""}`}
                  style={
                    codeError ? { color: errorColor ?? TITLE_COLOR } : undefined
                  }
                >
                  {codeError ?? <Trans ns="consumer-auth" i18nKey="codeHint" />}
                </BodyCopy>
                <OtpCodeInput
                  value={code}
                  onChange={setCode}
                  textColor={bodyCopy}
                  borderGradient={backgroundBtn}
                  fillColor={background}
                  invalid={Boolean(codeError)}
                  errorColor={errorColor}
                  ariaLabel={t("code")}
                  forceActive={exporting}
                />
                <div className="otp-resend">
                  {canResend ? (
                    <button type="button" className="consumer-auth-switch">
                      <GradientText
                        as="span"
                        textColor={backgroundBtn}
                        className="consumer-auth-switch-action"
                      >
                        {t("resendCode")}
                      </GradientText>
                    </button>
                  ) : (
                    <BodyCopy
                      as="p"
                      textColor={footerColor}
                      className="otp-resend-wait"
                    >
                      {t("resendIn", { time: waitClock })}
                    </BodyCopy>
                  )}
                </div>
              </div>
            ) : (
              <div className="mock-card-field">
                                <FloatingField
                  label={t("email")}
                  placeholder={t("emailPlaceholder")}
                  value={email}
                  colors={fieldColors}
                >
<input
                  id="consumer-auth-email"
                  style={{ color: bodyCopy }}
                  type="email"
                  autoComplete="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                />
                </FloatingField>
              </div>
            )}

            {formError && !isCode && (
              <BodyCopy
                as="p"
                textColor={errorColor ?? TITLE_COLOR}
                className="consumer-auth-error"
                style={{ color: errorColor ?? TITLE_COLOR }}
              >
                {formError}
              </BodyCopy>
            )}

            <div id="clerk-captcha" className="consumer-auth-captcha" />
          </div>
        </section>
      </Layout.Content>
      <Layout.Footer>
        <section className="footer-section consumer-auth-footer" ref={verifyCtaRef}>
          <Button
            size="large"
            background={backgroundBtn}
            color={textColorBtn}
            disabled={isCtaDisabled}
            onClick={() =>
              navigate(isCode ? CONSUMER_AUTH_ROUTES.BINDING : CONSUMER_AUTH_ROUTES.CODE)
            }
          >
            {isCode ? t("verify") : t("continue")}
          </Button>
        </section>
      </Layout.Footer>
    </AuraLayout>
  );
});

export const AuthSignInPage = memo(() => <AuthPage variant="form" />);
export const AuthSignInAfterLogoutPage = memo(() => (
  <AuthPage variant="formAfterLogout" />
));
export const AuthSignInBindFailedPage = memo(() => (
  <AuthPage variant="formBindFailed" />
));
export const AuthCodePage = memo(() => <AuthPage variant="code" />);
export const AuthCodeResendReadyPage = memo(() => (
  <AuthPage variant="codeResendReady" />
));
export const AuthCodeIncorrectPage = memo(() => (
  <AuthPage variant="codeIncorrect" />
));
export const AuthBindingPage = memo(() => <AuthPage variant="binding" />);
export const AuthMissingKeyPage = memo(() => <AuthPage variant="missingKey" />);
export const AuthPendingPage = memo(() => <AuthPage variant="pending" />);
export const AuthErrorBindPage = memo(() => <AuthPage variant="errorBind" />);
export const AuthErrorGenericPage = memo(() => (
  <AuthPage variant="errorGeneric" />
));
